/**
 * How it works — one worked example.
 *
 * The landing has five seconds to make a claim; this page has as long as the reader
 * wants to make it checkable. It used to carry four instruments — the call path, the
 * compile pipeline, the context ladder and the privacy boundary — one after another,
 * and four diagrams in a row is a page nobody finishes. A reader who has not yet
 * decided whether they believe the claim does not want four demonstrations; they want
 * one they can operate, and a door to the rest.
 *
 * So this page is one interactive example: the same task, run two ways, with the tokens
 * counted. The other three live on /examples for the reader who wants them.
 *
 * Two words carry the honesty and are used consistently in the copy: **measured**
 * figures come from the published corpus and are reproducible from the public
 * repository; **illustrative** animations use the real sequence with round per-step
 * numbers. A page that blurred the two would be the fastest way to lose an audience
 * that checks.
 */

import { Link } from 'react-router-dom'

import { Footer, Masthead } from '@/shared/ui/Chrome'
import { Label } from '@/shared/ui/primitives'
import { ContextLadder } from '@/shared/ui/walkthrough'

export function HowItWorks() {
  return (
    <>
      <Masthead />
      <main id="main">
        <section className="shell" style={{ paddingBlock: 'var(--s8) var(--s6)' }}>
          <div className="stack" style={{ ['--gap' as string]: 'var(--s5)', maxWidth: '54rem' }}>
            <Label index="00">How it works</Label>
            <h1 style={{ fontSize: 'clamp(2.2rem, 5vw, 3.4rem)' }}>
              See what your agent <span className="accent-text">actually reads</span>.
            </h1>
            <p className="lead" style={{ maxWidth: '54ch' }}>
              Pick a task below and watch the same job run twice: once by an agent reading
              your files, once by the same agent asking Archis. The token count on each side is
              the whole argument.
            </p>
            <div className="row" style={{ gap: 'var(--s3)', flexWrap: 'wrap' }}>
              <Link to="/docs/install" className="cta cta--primary">
                Install free →
              </Link>
              <Link to="/whitepaper" className="cta">
                Read the whitepaper and benchmarks →
              </Link>
            </div>
            <p className="mono-note" style={{ maxWidth: '80ch' }}>
              Everything on this page runs in your browser. Nothing is uploaded, no
              repository is read, and no API is called — which is the same promise the
              product makes, honoured by the page that makes it.{' '}
              <Link to="/verify">Check the product&apos;s claims yourself →</Link>
            </p>
          </div>
        </section>

        {/* The one interactive example. */}
        <section className="shell" style={{ paddingBottom: 'var(--s6)' }}>
          <div className="stack" style={{ ['--gap' as string]: 'var(--s5)' }}>
            <div className="stack" style={{ ['--gap' as string]: 'var(--s3)' }}>
              <Label index="01">The example</Label>
              <h2 style={{ maxWidth: '32ch' }}>
                One task, <span className="accent-text">two agents</span>
              </h2>
              <p className="dim" style={{ maxWidth: '68ch' }}>
                Archis serves context in escalating levels: facts about your code first, then
                compact views of the relevant symbols inside a budget you set, and exact
                source only when the job genuinely needs it. Turn the budget down and you
                can watch the trade-off appear.
              </p>
            </div>
            <ContextLadder />
            <p style={{ maxWidth: '72ch' }}>
              Measured: across 20 paired tasks per repository on 20 real repositories in 8
              languages, the full policy shows the agent 89.4% to 95.0% less context than a
              full-source agent doing the same task, 92.0% on average. That is context shown,
              not the bill. In real Claude Code sessions, where the agent also pays for its
              reasoning and its answer, the saving is smaller:{' '}
              <Link to="/verify#benchmark">22% of tokens in a typical session</Link>.
            </p>
          </div>
        </section>

        {/* The door to the rest, given its own block so it cannot be mistaken for a
            footnote under the example above. */}
        <section className="shell" style={{ paddingBottom: 'var(--s8)' }}>
          <div className="panel">
            <div className="row row--between" style={{ gap: 'var(--s5)' }}>
              <div className="stack" style={{ ['--gap' as string]: 'var(--s2)', maxWidth: '48ch' }}>
                <h3 style={{ fontFamily: 'var(--display)', fontSize: '1.125rem' }}>
                  Want to see more?
                </h3>
                <p className="dim" style={{ margin: 0, fontSize: '0.9375rem' }}>
                  Three more interactive examples: the full call path from question to
                  answer, what it costs to index a real repository, and exactly what
                  crosses the boundary between your machine and us.
                </p>
              </div>
              <Link to="/examples" className="cta" style={{ flex: 'none' }}>
                See more examples →
              </Link>
            </div>
          </div>
        </section>

        <section className="shell" style={{ paddingBottom: 'var(--s8)' }}>
          <div className="stack" style={{ ['--gap' as string]: 'var(--s5)' }}>
            <div className="stack" style={{ ['--gap' as string]: 'var(--s3)' }}>
              <Label index="02">The boundary</Label>
              <h2 style={{ maxWidth: '30ch' }}>Everything semantic stays on your machine</h2>
              <p className="dim" style={{ maxWidth: '68ch' }}>
                Indexing, retrieval and the dashboard are local. Only the commercial side —
                sign-in, billing, licence issuance, update checks — talks to us. That split
                is not a preference; it is the product&apos;s central claim, and it holds
                because there is no endpoint that could receive source code.
              </p>
            </div>

            <div className="grid-3" style={{ gap: 'var(--s4)' }}>
              <div className="card">
                <h4>Verified offline, every time</h4>
                <p className="dim" style={{ marginTop: 'var(--s2)', fontSize: '0.9375rem' }}>
                  The licence is a signed payload the CLI checks locally on every protected
                  command. Every deadline inside it is clamped to when access actually ends,
                  so an offline grace window can never outlive the subscription that granted
                  it.
                </p>
              </div>
              <div className="card">
                <h4>Leaks made unrepresentable</h4>
                <p className="dim" style={{ marginTop: 'var(--s2)', fontSize: '0.9375rem' }}>
                  Telemetry is opt-in and off until you enable it. Paths, symbols, queries and
                  error text cannot be attached to an event even by mistake, and numbers
                  travel only as coarse buckets. The server validates against its own
                  allowlist regardless.
                </p>
              </div>
              <div className="card">
                <h4>The honest consequence</h4>
                <p className="dim" style={{ marginTop: 'var(--s2)', fontSize: '0.9375rem' }}>
                  Because indexing is local, we cannot pre-warm your repository on a fleet of
                  servers or share an index between teammates through us. The first index runs
                  on your hardware. That is the cost of the guarantee, and it is the trade we
                  chose.
                </p>
              </div>
            </div>

            <Link to="/examples#boundary" className="cta" style={{ alignSelf: 'flex-start' }}>
              Trace what crosses the boundary →
            </Link>
          </div>
        </section>

        <section className="shell" style={{ paddingBottom: 'var(--s8)' }}>
          <div className="panel" style={{ textAlign: 'center', padding: 'var(--s7) var(--s5)' }}>
            <h2 style={{ maxWidth: '24ch', margin: '0 auto' }}>Run it on your own repository</h2>
            <p className="dim" style={{ maxWidth: '52ch', margin: 'var(--s4) auto var(--s5)' }}>
              <code>chi measure ./src</code> reproduces the reduction on your code in about a
              minute, on Archis Free.
            </p>
            <div className="row" style={{ gap: 'var(--s3)', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/docs/install" className="cta cta--primary">
                Install free →
              </Link>
              <Link to="/pricing" className="cta">
                Team and Enterprise plans →
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  )
}
